import Loading from "@/src/components/Loading";
import { toasts } from "@/src/components/toasts/toasts";
import { Switch } from "@/src/components/ui/switch";
import {
  AccountDocument,
  AccountQuery,
  AccountQueryVariables,
  useDealQuery,
  useUpdateDealMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { actions } from "@/src/store/auth/slice";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  SettingsCard,
  SettingsPage,
  SettingsRow,
} from "@/src/components/settings/SettingsLayout";
import { PermissionLevels } from "./PermissionLevels";

export function IndexingSettings() {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const client = useGqlClient();
  const updateDealMutation = useUpdateDealMutation(client);

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const dealQuery = useDealQuery(
    client,
    {
      id: activeDealId,
    },
    {
      enabled: activeDealId !== "",
    },
  );

  if (dealQuery.error) {
    return (
      <p className="text-sm text-muted-foreground">
        Error loading Data Room settings
      </p>
    );
  }

  if (dealQuery.isLoading || !dealQuery.data) {
    return <Loading />;
  }

  const handleToggleIndexing = (checked: boolean) => {
    updateDealMutation.mutate(
      {
        input: {
          id: activeDealId ?? "",
          indexing: checked,
        },
      },
      {
        onSuccess: () => {
          toasts.success(
            checked
              ? "Indexing enabled for this Data Room"
              : "Indexing disabled for this Data Room",
          );
          queryClient.invalidateQueries({
            queryKey: ["Deal", { id: activeDealId ?? "" }],
          });
          client
            .request<AccountQuery, AccountQueryVariables>(AccountDocument)
            .then((res) => {
              if (res.account) {
                dispatch(actions.refreshAccount({ account: res.account }));
              }
            });
        },
        onError: () => {
          toasts.error("Failed to update indexing settings");
        },
      },
    );
  };

  return (
    <SettingsPage
      title="Data Room"
      description="Configure settings for this deal's Data Room."
    >
      <SettingsCard title="General">
        <SettingsRow className="items-center">
          <div className="space-y-1 min-w-0 pr-4">
            <label
              htmlFor="enable-indexing"
              className="text-sm font-medium text-foreground"
            >
              Indexing
            </label>
            <p className="text-sm text-muted-foreground">
              When enabled, documents and folders are numbered automatically.
            </p>
          </div>
          <Switch
            id="enable-indexing"
            checked={dealQuery.data.deal.indexing}
            disabled={updateDealMutation.isPending}
            onCheckedChange={handleToggleIndexing}
          />
        </SettingsRow>
      </SettingsCard>

      <PermissionLevels />
    </SettingsPage>
  );
}
